import {
  ReactSketchCanvas,
  type ReactSketchCanvasRef,
} from "react-sketch-canvas";
import { type ChangeEvent, forwardRef, RefObject, useEffect, useRef, useState } from "react";
import { Eraser, MoveVertical, ChevronsDownUp, Pen, RefreshCcw, ScanText, Loader2 } from "lucide-react";
import './css/Button.css'
import { useTranslation } from "react-i18next";

const iconButton =
  "size-9 flex items-center justify-center rounded-[10px] cursor-pointer dark:border-accent-900 dark:text-accent-200";
const defaultIconButton =
  "bg-transparent text-accent-900 hover:bg-primary hover:text-white dark:hover:bg-accent-800";

interface AnswerCanvasProps {
  canvasId: string;
  onOcrRecognize?: () => void;
  isOcrProcessing?: boolean;
}

export const AnswerCanvas = forwardRef<ReactSketchCanvasRef, AnswerCanvasProps>((prop, ref) => {
  const [eraser, setEraser] = useState(false);
  const [strokeColor, setStrokeColor] = useState("#000000");
  // 输入区按屏高分档: 横屏(≤500) 90 / 矮屏(<1000) 200 / 高屏 260, 与键盘输入框一致;该初始值同时作为缩短下限
  const minHeightRef = useRef(window.innerHeight <= 500 ? 90 : window.innerHeight >= 1000 ? 260 : 200);
  const [height, setHeight] = useState(minHeightRef.current);
  const { t } = useTranslation();

  const handleEraserClick = () => {
    setEraser(true);
    if (ref?.current) {
      ref.current.eraseMode(true);
    }
  };

  const handlePencilClick = () => {
    setEraser(false);
    if (ref.current) {
      ref.current.eraseMode(false);
    }
  };

  const handleResetClick = () => {
    if (ref.current) {
      ref.current.resetCanvas();
    }
  };

  const handleExpandClick = () => {
    if (ref.current) {
      setHeight(height + 60);
    }
  };

  // 缩短: 与加长同步长(60px),下限=初始默认高度——只撤销之前的加长,默认高度时不再变矮
  const handleShrinkClick = () => {
    if (ref.current) {
      setHeight((h) => Math.max(minHeightRef.current, h - 60));
    }
  };

  const pencilSelected = !eraser
    ? "bg-[#9CEA4E] text-black hover:bg-[#9CEA4E] hover:text-black"
    : defaultIconButton;

  const eraserSelected = eraser
    ? "bg-[#9CEA4E] text-black hover:bg-[#9CEA4E] hover:text-black"
    : defaultIconButton;

  const onColorChange = (event: ChangeEvent<HTMLInputElement>) => {
    setStrokeColor(event.target.value);
  };

  return (
    <div className="reset-wrapper flex flex-col flex-1">
      <div className="relative rounded-[15px] overflow-hidden">
        {/* 工具栏 - absolute 右上,与画布共享同一个虚线框 */}
        <div className="absolute right-0.5 top-0.5 z-10 flex flex-row gap-0.5 items-center">
          {/* 色块占与图标按钮同宽的 36px 方格;mr 补偿选中笔绿底块的实心填充,避免与色块挤一起 */}
          <div className="size-9 flex items-center justify-center mr-1">
            <div className="w-[26px] h-[26px] rounded-full overflow-hidden">
              <input
                title="Color"
                className="w-[200%] h-[200%] bg-transparent border-none cursor-pointer appearance-none transform-cpu -translate-x-1/4 -translate-y-1/4"
                type="color"
                value={strokeColor}
                onChange={onColorChange}
              />
            </div>
          </div>
          <button
            title="Pencil"
            className={`${iconButton} ${pencilSelected} border-none`}
            type="button"
            aria-label="pencil"
            onClick={handlePencilClick}
          >
            <Pen size={20} />
          </button>
          <button
            title="Eraser"
            className={`${iconButton} ${eraserSelected} border-none`}
            type="button"
            aria-label="eraser"
            onClick={handleEraserClick}
          >
            <Eraser size={22} />
          </button>
          {/* ↕ 加长(原图标) + 双箭头合拢=缩短;两者笔画都窄,收窄此格宽度(w-7)抵消左右多余留白,使间隙与其它图标一致 */}
          <button
            title={t('component.expand')}
            className={`${iconButton} ${defaultIconButton} relative border-none w-7!`}
            type="button"
            aria-label="expand"
            onClick={handleExpandClick}
          >
            <MoveVertical size={22} />
          </button>
          <button
            title={t('component.shrink')}
            className={`${iconButton} ${defaultIconButton} relative border-none w-7!`}
            type="button"
            aria-label="shrink"
            onClick={handleShrinkClick}
          >
            <ChevronsDownUp size={22} />
          </button>
          <button
            title={t('component.reset')}
            className={`${iconButton} ${defaultIconButton} relative border-none`}
            type="button"
            aria-label="clear"
            onClick={handleResetClick}
          >
            <RefreshCcw size={22} />
          </button>
          <button
            title={t('component.convertOCR', { defaultValue: t('component.ocrRecognize', { defaultValue: 'OCR识别' }) })}
            className={`${iconButton} ${defaultIconButton} relative flex items-center`}
            type="button"
            aria-label="ocr-recognize"
            onClick={prop.onOcrRecognize}
            disabled={prop.isOcrProcessing}
          >
            {prop.isOcrProcessing ? <Loader2 className="animate-spin" size={22} /> : <ScanText size={22} />}
          </button>
        </div>
        <ReactSketchCanvas
          id={prop.canvasId}
          ref={ref}
          canvasColor="transparent"
          height={`${height}px`}
          strokeWidth={4}
          eraserWidth={40}
          strokeColor={strokeColor}
          withTimestamp={true}
          // 关掉 ReactSketchCanvas 默认自带的 1px 灰边框,
          // 否则画布外会出现一圈实线,与外层白卡的虚线框冲突。
          style={{ border: "none" }}
        />
      </div>
    </div>
  );
})